import { type ApiContainerAction, type ApiOrder } from '@Model';
import { type AsyncStateUpdate } from '@synqo/frontend-component-library';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { type PlannedContainerGroup } from './containerActionGroups';
import { lineState } from './lineConfirmation';
import { type ExchangeContainerEntry } from './plannedContainerEntries';
import { useMarkContainerForBooking } from './useMarkContainerForBooking';

export interface ContainerLineConfirmations {
    isMarking: boolean;
    confirm: (actionId: string) => void;
    confirmGroup: (group: PlannedContainerGroup) => void;
    confirmExchange: (exchange: ExchangeContainerEntry) => void;
    confirmAll: (actionIds: string[]) => void;
    unconfirm: (actionId: string) => void;
    unconfirmGroup: (group: PlannedContainerGroup) => void;
    unconfirmExchange: (exchange: ExchangeContainerEntry) => void;
}

/** Confirms container lines and takes confirmations back, also when the completion date goes away. */
// @ai-generated 2026-10-15T07:18:24+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
export function useContainerLineConfirmations(
    orderId: string,
    mergeAction: (action: ApiContainerAction) => void,
    requestUpdateState: AsyncStateUpdate<ApiOrder>,
    refetchOrder: () => Promise<unknown>,
    containerActions: readonly ApiContainerAction[],
    completing: boolean,
): ContainerLineConfirmations {
    const { markForBooking, markGroupForBooking, unmarkGroupForBooking, isMarking } = useMarkContainerForBooking(
        orderId,
        mergeAction,
    );

    const mergeMoved = useCallback(
        async (moved: ApiContainerAction[] | undefined) => {
            if (moved === undefined || moved.length === 0) {
                return;
            }

            await requestUpdateState((latest) => ({
                data: {
                    ...latest,
                    container_actions: latest.container_actions.map(
                        (action) => moved.find((row) => row.id === action.id) ?? action,
                    ),
                },
                messages: [],
            }));
        },
        [requestUpdateState],
    );

    const wasCompleting = useRef(completing);
    useEffect(() => {
        if (wasCompleting.current && !completing) {
            const confirmed = containerActions.filter((action) => lineState(action.booking_status) === 'confirmed');
            void (async () => mergeMoved(await unmarkGroupForBooking(confirmed.map((action) => action.id))))();
        }
        wasCompleting.current = completing;
    }, [completing, containerActions, mergeMoved, unmarkGroupForBooking]);

    const thenRefetch = useCallback(
        (move: (actionId: string) => Promise<boolean>) => (exchange: ExchangeContainerEntry) => {
            void (async () => {
                if (await move(exchange.pickup.id)) {
                    await refetchOrder();
                }
            })();
        },
        [refetchOrder],
    );

    return useMemo(
        () => ({
            isMarking,
            confirm: (actionId) => void markForBooking(actionId),
            confirmGroup: (group) =>
                void (async () =>
                    mergeMoved(await markGroupForBooking(group.openActions.map((action) => action.id))))(),
            confirmExchange: thenRefetch(markForBooking),
            confirmAll: (actionIds) => void (async () => mergeMoved(await markGroupForBooking(actionIds)))(),
            unconfirm: (actionId) => void (async () => mergeMoved(await unmarkGroupForBooking([actionId])))(),
            unconfirmGroup: (group) => {
                const confirmed = group.actions.filter((action) => lineState(action.booking_status) === 'confirmed');
                void (async () => mergeMoved(await unmarkGroupForBooking(confirmed.map((action) => action.id))))();
            },
            unconfirmExchange: (exchange) =>
                void (async () => mergeMoved(await unmarkGroupForBooking([exchange.pickup.id])))(),
        }),
        [isMarking, markForBooking, markGroupForBooking, unmarkGroupForBooking, mergeMoved, thenRefetch],
    );
}
